import type { ReactNode } from "react";
import { Routes, Route } from "react-router-dom";
import { QueryClient, QueryClientProvider, HydrationBoundary } from "@tanstack/react-query";
import { HelmetProvider, type HelmetServerState } from "react-helmet-async";
import { AuthProvider } from "./contexts/AuthContext";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import ScrollToTop from "./components/ScrollToTop";
import { ChatWidget } from "./components/chat/ChatWidget";
import ProtectedRoute from "./components/ProtectedRoute";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import ServicesPage from "./pages/ServicesPage";
import BlogPage from "./pages/BlogPage";
import BlogPostPage from "./pages/BlogPostPage";
import BlogAdminPage from "./pages/admin/BlogAdminPage";
import MediaLibraryPage from "./pages/admin/MediaLibraryPage";
import FAQAdminPage from "./pages/admin/FAQAdminPage";
import LoginPage from "./pages/LoginPage";
import PressureWashingPage from "./pages/services/PressureWashingPage";
import WindowWashingPage from "./pages/services/WindowWashingPage";
import CarpetCleaningPage from "./pages/services/CarpetCleaningPage";
import GutterCleaningPage from "./pages/services/GutterCleaningPage";
import SolarPanelCleaningPage from "./pages/services/SolarPanelCleaningPage";
import SoftWashPage from "./pages/services/SoftWashPage";
import DumpsterPadCleaningPage from "./pages/services/DumpsterPadCleaningPage";
import ThousandOaksPage from "./pages/locations/ThousandOaksPage";
import CamarilloPage from "./pages/locations/CamarilloPage";
import OxnardPage from "./pages/locations/OxnardPage";
import FillmorePage from "./pages/locations/FillmorePage";
import MoorparkPage from "./pages/locations/MoorparkPage";
import PortHuenemePage from "./pages/locations/PortHuenemePage";
import SantaPaulaPage from "./pages/locations/SantaPaulaPage";
import SimiValleyPage from "./pages/locations/SimiValleyPage";
import VenturaPage from "./pages/locations/VenturaPage";
import FreeEstimatePage from "./pages/FreeEstimatePage";
import FAQPage from "./pages/FAQPage";
import AboutPage from "./pages/AboutPage";
import AreasPage from "./pages/AreasPage";
import NewburyParkPage from "./pages/locations/NewburyParkPage";
import WestlakeVillagePage from "./pages/locations/WestlakeVillagePage";
import OjaiPage from "./pages/locations/OjaiPage";

// Route tree only — no Router, no providers. Mounted inside a
// BrowserRouter (client, src/main.tsx) or a StaticRouter (server,
// src/entry-server.tsx), both of which supply their own Providers.
export const AppRoutes = () => (
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/login" element={<LoginPage />} />
    <Route path="/free-estimate" element={<FreeEstimatePage />} />
    <Route path="/services" element={<ServicesPage />} />

    {/* Blog Routes */}
    <Route path="/blog" element={<BlogPage />} />
    <Route path="/blog/:slug" element={<BlogPostPage />} />
    <Route
      path="/admin/blog"
      element={
        <ProtectedRoute requireAdmin={true}>
          <BlogAdminPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/media"
      element={
        <ProtectedRoute requireAdmin={true}>
          <MediaLibraryPage />
        </ProtectedRoute>
      }
    />
    <Route
      path="/admin/faqs"
      element={
        <ProtectedRoute requireAdmin={true}>
          <FAQAdminPage />
        </ProtectedRoute>
      }
    />
    <Route path="/services/pressure-washing" element={<PressureWashingPage />} />
    <Route path="/services/window-washing" element={<WindowWashingPage />} />
    <Route path="/services/carpet-cleaning" element={<CarpetCleaningPage />} />
    <Route path="/services/gutter-cleaning" element={<GutterCleaningPage />} />
    <Route path="/services/solar-panel-cleaning" element={<SolarPanelCleaningPage />} />
    <Route path="/services/soft-wash" element={<SoftWashPage />} />
    <Route path="/services/dumpster-pad-cleaning" element={<DumpsterPadCleaningPage />} />
    <Route path="/thousand-oaks" element={<ThousandOaksPage />} />
    <Route path="/camarillo" element={<CamarilloPage />} />
    <Route path="/oxnard" element={<OxnardPage />} />
    <Route path="/fillmore" element={<FillmorePage />} />
    <Route path="/moorpark" element={<MoorparkPage />} />
    <Route path="/port-hueneme" element={<PortHuenemePage />} />
    <Route path="/santa-paula" element={<SantaPaulaPage />} />
    <Route path="/simi-valley" element={<SimiValleyPage />} />
    <Route path="/ventura" element={<VenturaPage />} />
    <Route path="/faq" element={<FAQPage />} />
    <Route path="/about" element={<AboutPage />} />
    <Route path="/areas" element={<AreasPage />} />
    <Route path="/newbury-park" element={<NewburyParkPage />} />
    <Route path="/westlake-village" element={<WestlakeVillagePage />} />
    <Route path="/ojai" element={<OjaiPage />} />
    {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
    <Route path="*" element={<NotFound />} />
  </Routes>
);

interface ProvidersProps {
  children: ReactNode;
  queryClient: QueryClient;
  /** Server render: object populated with Helmet's collected head tags. */
  helmetContext?: { helmet?: HelmetServerState };
  /** Client hydration: React Query cache seeded during prerendering. */
  dehydratedState?: unknown;
}

// Shared provider stack used by both the browser entry (main.tsx, wrapped
// in BrowserRouter) and the server entry (entry-server.tsx, wrapped in
// StaticRouter). Keeping this in one place — including Toaster/Sonner/
// ScrollToTop, which render the same (empty) markup either side — is what
// lets prerendered HTML and the hydrated client agree on structure. A
// server/client mismatch here makes React discard the prerendered DOM on
// hydration, which defeats the point of prerendering for real users.
export const Providers = ({
  children,
  queryClient,
  helmetContext = {},
  dehydratedState,
}: ProvidersProps) => (
  <QueryClientProvider client={queryClient}>
    <HydrationBoundary state={dehydratedState}>
      <HelmetProvider context={helmetContext}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <ScrollToTop />
            {children}
            <ChatWidget />
          </TooltipProvider>
        </AuthProvider>
      </HelmetProvider>
    </HydrationBoundary>
  </QueryClientProvider>
);
